import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layouts
import AdminLayout from './components/common/AdminLayout';
import CustomerLayout from './components/common/CustomerLayout';

// Admin / Staff Pages
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AddCustomerPage from './pages/AddCustomerPage';
import PaymentsPage from './pages/PaymentsPage';
import RenewalsPage from './pages/RenewalsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import ComplaintDetailPage from './pages/ComplaintDetailPage';
import DevicesPage from './pages/DevicesPage';
import ACSPage from './pages/ACSPage';
import PlansPage from './pages/PlansPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import CashCollectionPage from './pages/CashCollectionPage';
import ScanPage from './pages/ScanPage';
import KYCPage from './pages/KYCPage';
import SearchPage from './pages/SearchPage';
import MoneyManagerPage from './pages/MoneyManagerPage';
import ImportPage from './pages/ImportPage';

// Customer Portal Pages
import CustomerLoginPage from './pages/customer/CustomerLoginPage';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerComplaints from './pages/customer/CustomerComplaints';
import CustomerPayments from './pages/customer/CustomerPayments';
import CustomerProfile from './pages/customer/CustomerProfile';
import CustomerWifi from './pages/customer/CustomerWifi';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) {
    if (user.role === 'customer') return <Navigate to="/portal" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" toastOptions={{ duration: 4000, style: { borderRadius: '8px', background: '#1e293b', color: '#f1f5f9' } }} />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/portal/login" element={<PublicRoute><CustomerLoginPage /></PublicRoute>} />

          {/* Admin / Staff Routes */}
          <Route path="/" element={<ProtectedRoute roles={['admin', 'staff']}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/add" element={<AddCustomerPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="renewals" element={<RenewalsPage />} />
            <Route path="complaints" element={<ComplaintsPage />} />
            <Route path="complaints/:id" element={<ComplaintDetailPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="acs" element={<ProtectedRoute roles={['admin']}><ACSPage /></ProtectedRoute>} />
            <Route path="plans" element={<ProtectedRoute roles={['admin']}><PlansPage /></ProtectedRoute>} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="users" element={<ProtectedRoute roles={['admin']}><UsersPage /></ProtectedRoute>} />
            <Route path="cash-collection" element={<CashCollectionPage />} />
            <Route path="money" element={<MoneyManagerPage />} />
            <Route path="scan" element={<ScanPage />} />
            <Route path="kyc" element={<KYCPage />} />
            <Route path="search-customer" element={<SearchPage />} />
            <Route path="import" element={<ProtectedRoute roles={['admin']}><ImportPage /></ProtectedRoute>} />
          </Route>

          {/* Customer Portal Routes */}
          <Route path="/portal" element={<ProtectedRoute roles={['customer']}><CustomerLayout /></ProtectedRoute>}>
            <Route index element={<CustomerDashboard />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="wifi" element={<CustomerWifi />} />
            <Route path="complaints" element={<CustomerComplaints />} />
            <Route path="payments" element={<CustomerPayments />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
